import React, { forwardRef } from "react";
import MaterialTable from "material-table";
import AddBox from "@material-ui/icons/AddBox";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import Check from "@material-ui/icons/Check";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Clear from "@material-ui/icons/Clear";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Edit from "@material-ui/icons/Edit";
import FilterList from "@material-ui/icons/FilterList";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import Remove from "@material-ui/icons/Remove";
import SaveAlt from "@material-ui/icons/SaveAlt";
import Search from "@material-ui/icons/Search";
import ViewColumn from "@material-ui/icons/ViewColumn";
import AddToQueueIcon from "@material-ui/icons/AddToQueue";
import { Chip } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import TodayIcon from "@material-ui/icons/Today";
import useBDocTranslator from "../hooks/useBDocTranslator";
import useUserState from "../hooks/useUserState";
import { getPharmacyTypeLabel } from "../../utils/pharmacyValidation";

const tableIcons = {
  AddToQueue: forwardRef((props, ref) => (
    <AddToQueueIcon {...props} ref={ref} />
  )),
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  CalendarToday: forwardRef((props, ref) => <TodayIcon {...props} ref={ref} />),
};

function TablePharmacy({ translator, data, clickEvent, onSelectionChange }) {
  let history = useHistory();
  const { t } = useBDocTranslator();
  const userState = useUserState();

  // only the admin can edit the places
  const isAdmin =
    (userState?.[0]?.user?.role?.name || "").toUpperCase() === "ADMIN";

  const handleAddAppointment = (row) => {
    history.push({
      pathname: "/admin/newAppointment",
      state: { patient: row },
    });
  };

  return (
    <div style={{ maxWidth: "100%" }}>
      <MaterialTable
        columns={[
          { title: "Id", field: "id" },
          { title: t("Name"), field: "name" },
          { title: t("Country"), field: "country" },
          {
            title: t("City"),
            field: "city",
          },
          { title: t("Phone"), field: "phone" },
          {
            title: t("Type"),
            field: "type",
            render: (row) => getPharmacyTypeLabel(row.type, t),
          },
          {
            title: t("Agreement"),
            field: "agreement_code",
            render: (row) =>
              row.agreement_code ? (
                <Chip
                  label={row.agreement_code}
                  size="small"
                  style={{
                    height: 26,
                    fontFamily:
                      "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    color: "#085279",
                    background: "#eaf3fa",
                    border: "1px solid #cfe4f2",
                  }}
                />
              ) : (
                <span style={{ color: "#b6c4ce" }}>—</span>
              ),
          },
          {
            title: t("Register date"),
            field: "created_at",
            type: "datetime",
          },
          {
            title: t("Status"),
            field: "status",
            lookup: {
              enabled: t("Enabled"),
              disabled: t("Disabled"),
            },
          },
          {
            title: t("E-mail"),
            field: "email",
          },
        ]}
        data={data}
        title=""
        icons={tableIcons}
        options={{
          headerStyle: {
            backgroundColor: "#01579b",
            color: "#FFF",
          },
          actionsColumnIndex: -1,
          // the places can be selected to assign the agreement code in bulk
          selection: true,
          showSelectAllCheckbox: true,
          // the contextual bar of the screen already shows the count
          showTextRowsSelected: false,
        }}
        onSelectionChange={onSelectionChange}
        actions={
          isAdmin
            ? [
                {
                  icon: Edit,
                  tooltip: t("Edit"),
                  onClick: (e, row) => clickEvent(row),
                  // with the selection enabled the actions move to the toolbar
                  // unless they are pinned to the row
                  position: "row",
                },
              ]
            : []
        }
        localization={{
          body: {
            emptyDataSourceMessage: t("No records to display"),
          },
          header: {
            actions: t("Actions"),
          },
          toolbar: {
            searchPlaceholder: t("Search"),
            // material-table accepts a function here, so the count is
            // translated with the same plural rules as the rest of the app
            nRowsSelected: (count) => t("N places selected", { count }),
          },
        }}
      />
    </div>
  );
}

export default TablePharmacy;
